import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp, labelOf } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { formatSAR, formatIDR } from "@/lib/utils";
import { toast } from "sonner";
import { Plane, Landmark, Route as RouteIcon, Users, Plus, Trash2 } from "lucide-react";

const SERVICE_ICON = { airport_pickup: Plane, city_tour: Landmark, intercity: RouteIcon };
const newTrip = (offsetDays = 1) => ({
  service_type: "airport_pickup", route: "Bandara Jeddah → Makkah", vehicle_type: "hiace",
  pickup_date: new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 16), pickup_location: "", passengers: 4, notes: "",
});

function TripCard({ idx, trip, catalog, price, onChange, onRemove, canRemove, t }) {
  const vt = catalog.vehicle_types;
  const set = (patch) => onChange({ ...trip, ...patch });
  return (
    <Card data-testid={`trip-card-${idx}`}><CardContent className="p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold">{t("trip")} {idx + 1}{price != null && <span className="ml-2 text-emerald-700">{formatSAR(price)}</span>}</div>
        {canRemove && <Button type="button" size="sm" variant="ghost" onClick={onRemove} data-testid={`trip-remove-${idx}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {Object.keys(catalog.services).map((k) => { const I = SERVICE_ICON[k] || RouteIcon;
          return (
            <button type="button" key={k} onClick={() => set({ service_type: k, route: catalog.services[k].routes[0] || "" })} data-testid={`svc-${k}${idx ? `-${idx}` : ""}`}
              className={`text-left rounded-xl border p-4 transition ${trip.service_type === k ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200" : "border-slate-200 hover:border-emerald-300"}`}>
              <I className="w-5 h-5 text-emerald-700 mb-2" /><div className="font-semibold text-sm">{labelOf(t, `svc_${k}`, catalog.services[k].label)}</div>
            </button>);
        })}
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <div><Label>{t("route")}</Label>
          <Select value={trip.route} onValueChange={(v) => set({ route: v })}>
            <SelectTrigger data-testid={`booking-route${idx ? `-${idx}` : ""}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{(catalog.services[trip.service_type]?.routes || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>{t("vehicle_type")}</Label>
          <Select value={trip.vehicle_type} onValueChange={(v) => set({ vehicle_type: v })}>
            <SelectTrigger data-testid={`booking-vehicle${idx ? `-${idx}` : ""}`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{Object.entries(vt).map(([k, v]) => <SelectItem key={k} value={k}>{v.label} · {v.capacity} pax</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>{t("pickup_date")}</Label>
          <Input type="datetime-local" value={trip.pickup_date} onChange={(e) => set({ pickup_date: e.target.value })} required data-testid={`booking-date${idx ? `-${idx}` : ""}`} /></div>
        <div><Label>{t("passengers")}</Label>
          <Input type="number" min={1} max={vt[trip.vehicle_type]?.capacity || 50} value={trip.passengers} onChange={(e) => set({ passengers: +e.target.value })} data-testid={`booking-passengers${idx ? `-${idx}` : ""}`} /></div>
        <div><Label>{t("pickup_location")}</Label>
          <Input value={trip.pickup_location} onChange={(e) => set({ pickup_location: e.target.value })} placeholder={t("pickup_location_ph")} data-testid={`booking-pickup-location${idx ? `-${idx}` : ""}`} /></div>
        <div><Label>{t("notes")}</Label><Input value={trip.notes} onChange={(e) => set({ notes: e.target.value })} data-testid={`booking-notes${idx ? `-${idx}` : ""}`} /></div>
      </div>
    </CardContent></Card>
  );
}

export default function BookingWizard() {
  const { t, user } = useApp();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const packageId = sp.get("package");
  const [catalog, setCatalog] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [trips, setTrips] = useState([newTrip()]);
  const [c, setC] = useState({ customer_name: user?.name || "", customer_email: user?.email || "", customer_phone: user?.phone || "" });
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    try { const { data } = await api.get(`/public/promo/${encodeURIComponent(promoInput.trim())}`); setPromo(data); toast.success(`${t("promo_applied")}: ${data.code} -${data.type === "amount" ? formatSAR(data.amount) : `${data.percent}%`}`); }
    catch { setPromo(null); toast.error(t("promo_invalid")); }
  };
  useEffect(() => {
    api.get("/public/catalog").then((r) => {
      setCatalog(r.data);
      const p = packageId ? (r.data.settings.packages || []).find((x) => x.id === packageId) : null;
      if (p) { setPkg(p); setTrips(p.trips.map((tr, i) => ({ ...newTrip(i + 1), ...tr, vehicle_type: "hiace" }))); }
    });
  }, [packageId]);

  const prices = useMemo(() => catalog ? trips.map((tr) => catalog.settings.prices[`${tr.service_type}|${tr.route}|${tr.vehicle_type}`] ?? null) : [], [catalog, trips]);
  const quote = useMemo(() => {
    if (!catalog || prices.some((p) => p == null)) return null;
    const s = catalog.settings; const subtotal = prices.reduce((a, b) => a + b, 0);
    const pkgOk = pkg && trips.length >= pkg.trips.length;
    const fixed = pkgOk && pkg.price_mode === "fixed" ? pkg.fixed_prices?.[trips[0].vehicle_type] : null;
    const discount = fixed > 0 ? Math.max(0, subtotal - fixed) : pkgOk ? Math.round(subtotal * (pkg.discount_percent || 0)) / 100 : 0;
    const pct = subtotal > 0 ? Math.round((discount / subtotal) * 1000) / 10 : 0;
    const afterPkg = subtotal - discount;
    const promoPct = promo?.percent || 0;
    const promoDiscount = promo ? (promo.type === "amount" ? Math.min(afterPkg, promo.amount || 0) : Math.round(afterPkg * promoPct) / 100) : 0;
    const price = afterPkg - promoDiscount;
    const dp = Math.round(price * s.dp_percent) / 100;
    return { subtotal, discount, pct, promoPct, promoDiscount, price, dp, dp_idr: Math.round(dp * s.idr_rate), dpPct: s.dp_percent, rate: s.idr_rate };
  }, [catalog, prices, pkg, trips.length, promo]);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post(user ? "/bookings" : "/public/booking", { ...c, trips, package_id: pkg?.id || null, promo_code: promo?.code || null });
      toast.success(`${t("booking_created")}: ${data.booking.code}`);
      nav(`/invoice/${data.booking.code}`);
    } catch (err) { toast.error(err?.response?.data?.detail || t("booking_failed")); }
    finally { setLoading(false); }
  };
  if (!catalog) return <div className="py-20 text-center text-slate-500">{t("loading")}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold mb-2">{t("book_now")}</h1>
      <p className="text-slate-500 mb-8">{t("booking_intro")}</p>
      {pkg && <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="package-banner">
        <div><div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">{t("packages")}</div><div className="font-bold text-lg">{pkg.name}</div><div className="text-sm text-slate-600">{pkg.desc}</div></div>
        <div className="text-emerald-700 font-bold">{pkg.price_mode === "fixed" ? t("price_mode_fixed") : t("save_pct").replace("{n}", pkg.discount_percent)}</div></div>}
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {trips.map((tr, i) => (
            <TripCard key={i} idx={i} trip={tr} catalog={catalog} price={prices[i]} t={t} canRemove={trips.length > 1}
              onChange={(nt) => setTrips(trips.map((x, j) => (j === i ? nt : x)))} onRemove={() => setTrips(trips.filter((_, j) => j !== i))} />
          ))}
          <Button type="button" variant="outline" className="w-full border-dashed" onClick={() => setTrips([...trips, newTrip(trips.length + 1)])} data-testid="add-trip">
            <Plus className="w-4 h-4 mr-2" />{t("add_trip")}
          </Button>
          <Card><CardContent className="p-6 grid md:grid-cols-3 gap-4">
            <div className="md:col-span-3 font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-emerald-700" />{t("contact_details")}</div>
            <div><Label>{t("name")}</Label><Input value={c.customer_name} onChange={(e) => setC({ ...c, customer_name: e.target.value })} required data-testid="booking-name" /></div>
            <div><Label>{t("email")}</Label><Input type="email" value={c.customer_email} onChange={(e) => setC({ ...c, customer_email: e.target.value })} required data-testid="booking-email" /></div>
            <div><Label>{t("whatsapp")}</Label><Input value={c.customer_phone} onChange={(e) => setC({ ...c, customer_phone: e.target.value })} placeholder="+62 812 xxxx xxxx" required data-testid="booking-phone" /></div>
          </CardContent></Card>
        </div>
        <Card className="lg:sticky lg:top-24 h-fit"><CardContent className="p-6 space-y-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("summary")}</div>
          <div className="text-sm space-y-2 text-slate-600" data-testid="summary-trips">
            {trips.map((tr, i) => (
              <div key={i} className="flex justify-between gap-2"><div><div className="font-semibold text-slate-900">{tr.route}</div><div className="text-xs">{catalog.vehicle_types[tr.vehicle_type]?.label || tr.vehicle_type} · {tr.passengers} pax</div></div>
                <div className="font-semibold whitespace-nowrap">{prices[i] != null ? formatSAR(prices[i]) : "-"}</div></div>))}
          </div>
          <div className="border-t pt-4">
            <div className="flex gap-2 mb-3">
              <Input className="h-9 uppercase" placeholder={t("have_promo")} value={promoInput} onChange={(e) => setPromoInput(e.target.value)} data-testid="promo-input" />
              <Button type="button" variant="outline" className="h-9" onClick={applyPromo} data-testid="promo-apply">{t("apply")}</Button>
            </div>
            {(quote?.discount > 0 || quote?.promoDiscount > 0) && <div className="text-sm space-y-1 mb-2" data-testid="booking-discount">
              <div className="flex justify-between text-slate-600"><span>{t("subtotal")}</span><span>{formatSAR(quote.subtotal)}</span></div>
              {quote.discount > 0 && <div className="flex justify-between text-rose-600 font-semibold"><span>{t("package_discount")} {quote.pct}%</span><span>- {formatSAR(quote.discount)}</span></div>}
              {quote.promoDiscount > 0 && <div className="flex justify-between text-rose-600 font-semibold" data-testid="booking-promo-discount"><span>{promo.code} {promo.type === "amount" ? "" : `${quote.promoPct}%`}</span><span>- {formatSAR(quote.promoDiscount)}</span></div>}</div>}
            <div className="text-xs uppercase text-slate-500">{t("total")} ({trips.length} {t("trip").toLowerCase()})</div>
            <div className="text-3xl font-bold text-emerald-700" data-testid="booking-price">{quote ? formatSAR(quote.price) : "-"}</div>
          </div>
          {quote && <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm" data-testid="booking-dp">
            <div className="font-semibold text-amber-900">{t("dp")} {quote.dpPct}% = {formatSAR(quote.dp)}</div>
            <div className="text-amber-800">≈ {formatIDR(quote.dp_idr)} <span className="text-xs">(1 SAR = {formatIDR(quote.rate)})</span></div>
            <div className="text-xs text-amber-700 mt-1">{t("dp_note")}</div>
          </div>}
          <Button type="submit" disabled={loading || !quote} size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="booking-submit">{loading ? "..." : t("confirm_booking")}</Button>
        </CardContent></Card>
      </form>
    </div>
  );
}
